import { useEffect, useRef, useState } from 'react';
import { hydrate, notifyManager, useQueryClient } from 'react-query';
import type { Query, QueryClient, QueryKey } from 'react-query';

/**
 * The new StrengthX's lists and reference data, kept in this browser between
 * visits so a page opens with what it showed last time and then refreshes in
 * the background (stale-while-revalidate).
 *
 * - Only the queries in PERSISTED are kept: lists, counts and settings that the
 *   page shows anyway. No chat thread, no token, no session list: a chat
 *   inbox keeps its previews (the last message's first words), nothing more.
 * - One person's data at a time, keyed by who is signed in and whose clients
 *   they work on (a team member's coach). Signing out, another person
 *   signing in and a new CACHE_VERSION all remove it.
 * - What comes back is marked out of date, so every query refreshes the
 *   first time a page uses it; anything older than MAX_AGE is dropped.
 * - It lives in localStorage, within a size budget: the newest entries win,
 *   one entry over MAX_ENTRY is never kept, and if the browser refuses
 *   (full, private mode) the portal simply works as before.
 */

/** Bump whenever the shape of a persisted reply changes, so old copies are thrown away. */
export const CACHE_VERSION = 1;
export const STORAGE_PREFIX = 'sx-cache:';
export const MAX_AGE = 24 * 60 * 60_000;
/** Characters (localStorage counts UTF-16 code units, about 5 million per site). */
export const MAX_TOTAL = 1_500_000;
export const MAX_ENTRY = 400_000;
const MAX_ENTRIES = 150;
const SAVE_DELAY = 1_000;

type TKeyPart = string | number | boolean | null | undefined | Record<string, unknown>;

type TRule = {
  prefix: TKeyPart[];
  /** Whether this query (by its whole key) is kept. */
  keep?: (key: unknown[]) => boolean;
  /** Only put back on the day it was saved (it shows the date). */
  sameDay?: boolean;
};

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** A Clients list worth keeping: the first page without a search (To-do's and the Clients view's), not every search typed. */
const firstPageUnsearched = (key: unknown[]) => {
  const query = key[2];
  if (query === 'todo') return true;
  return isObject(query) && !String(query.q || '').trim() && (Number(query.page) || 1) === 1;
};

/** Every query kept between visits, by the start of its key. */
export const PERSISTED: TRule[] = [
  // Clients: the list (To-do's lists too) and every client's name for pickers and chat.
  { prefix: ['sx', 'clientList'], keep: firstPageUnsearched },
  { prefix: ['sx', 'chat', 'directory'] },
  // Chat: the inboxes and their conversation lists (previews only), not searches.
  { prefix: ['sx', 'chat', 'inboxes'] },
  { prefix: ['sx', 'chat', 'inbox'], keep: (key) => !String(key[4] || '').trim() },
  // Alerts and the menu's counts.
  { prefix: ['sx', 'alerts'] },
  { prefix: ['sx', 'alertSettings'] },
  { prefix: ['sx', 'metricsSwitch'] },
  // To-do and Home (Home's greeting names the day, so only that day's is put back).
  { prefix: ['askHome'], sameDay: true },
  { prefix: ['sx', 'triage'] },
  { prefix: ['sx', 'programs-needed'] },
  { prefix: ['sx', 'gone-quiet'] },
  { prefix: ['sx', 'todo-home'] },
  { prefix: ['sx', 'checkinsToday'] },
  // Nutrition.
  { prefix: ['sx', 'nutritionRoster'] },
  // Reference data.
  { prefix: ['userPreferences'] },
  { prefix: ['sx', 'coaching-rules'], keep: (key) => key.length === 2 },
  { prefix: ['sx', 'coaches'] },
  { prefix: ['sx', 'team'] },
  { prefix: ['sx', 'tags'] },
  { prefix: ['cueLibrary'] },
  // The groups list and, under it, the menu's count.
  { prefix: ['sx', 'communityGroups'] },
  { prefix: ['sx', 'templates'] },
  { prefix: ['sx', 'forms'] },
  { prefix: ['classroom', 'page'] },
];

const startsWith = (key: unknown[], prefix: TKeyPart[]) =>
  key.length >= prefix.length && prefix.every((part, i) => key[i] === part);

const ruleFor = (queryKey: QueryKey) => {
  const key = Array.isArray(queryKey) ? (queryKey as unknown[]) : [queryKey];
  const rule = PERSISTED.find((r) => startsWith(key, r.prefix));
  return rule && (!rule.keep || rule.keep(key)) ? rule : null;
};

/** Whether a query is kept between visits. */
export const isPersisted = (queryKey: QueryKey): boolean => Boolean(ruleFor(queryKey));

const dayOf = (time: number) => new Date(time).toDateString();

/** Whose data: the person signed in, and for a team member the coach whose clients they work on. */
export type TCacheOwner = { id?: string | null; subCoachDetails?: { trainerId?: string | null } | null } | null | undefined;

export const cacheScopeOf = (user: TCacheOwner): string | null => {
  if (!user?.id) return null;
  const coach = user.subCoachDetails?.trainerId;
  return coach && coach !== user.id ? `${user.id}@${coach}` : user.id;
};

export const storageKeyOf = (scope: string) => `${STORAGE_PREFIX}v${CACHE_VERSION}:${scope}`;

type TEntry = { k: unknown[]; h: string; d: unknown; t: number };
type TStored = { v: number; scope: string; savedAt: number; entries: TEntry[] };

const storage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const ourKeys = (store: Storage) => {
  const keys: string[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key && key.startsWith(STORAGE_PREFIX)) keys.push(key);
  }
  return keys;
};

// One session of persistence at a time: the client, whose data, and what is kept.
let client: QueryClient | null = null;
let activeScope: string | null = null;
/** The last person whose data was in memory, even after they signed out. */
let lastScope: string | null = null;
let entries = new Map<string, TEntry>();
/** Queries put back from storage this visit, with the time of the data they were given. */
const restored = new Map<string, number>();
let unsubscribe: (() => void) | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
const restoredListeners = new Set<() => void>();

/** Tells the "Updating…" hooks, as react-query tells its own: batched, after whatever is rendering now. */
const notifyRestored = () => notifyManager.schedule(() => restoredListeners.forEach((l) => l()));

/** Writes what is kept now, newest first, within the budget. */
export const saveNow = (): void => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const store = storage();
  if (!store || !activeScope) return;
  const now = Date.now();
  const fresh = Array.from(entries.values())
    .filter((e) => now - e.t < MAX_AGE)
    .sort((a, b) => b.t - a.t);
  const write = (budget: number) => {
    const kept: string[] = [];
    let used = 0;
    fresh.forEach((e) => {
      if (kept.length >= MAX_ENTRIES) return;
      let text: string;
      try {
        text = JSON.stringify(e);
      } catch {
        return;
      }
      if (text.length > MAX_ENTRY || used + text.length > budget) return;
      kept.push(text);
      used += text.length;
    });
    const head: Omit<TStored, 'entries'> = { v: CACHE_VERSION, scope: activeScope as string, savedAt: now };
    store.setItem(storageKeyOf(activeScope as string), `${JSON.stringify(head).slice(0, -1)},"entries":[${kept.join(',')}]}`);
  };
  try {
    write(MAX_TOTAL);
  } catch {
    // Full (other sites' data, or a small quota): try with half, else keep nothing.
    try {
      write(MAX_TOTAL / 2);
    } catch {
      try {
        store.removeItem(storageKeyOf(activeScope));
      } catch {
        // Nothing more to do: the portal works without it.
      }
    }
  }
};

const scheduleSave = () => {
  if (timer) return;
  timer = setTimeout(saveNow, SAVE_DELAY);
};

const remember = (query: Query) => {
  if (!activeScope || query.state.status !== 'success' || query.state.data === undefined || !isPersisted(query.queryKey)) return;
  entries.set(query.queryHash, {
    k: (Array.isArray(query.queryKey) ? query.queryKey : [query.queryKey]) as unknown[],
    h: query.queryHash,
    d: query.state.data,
    t: query.state.dataUpdatedAt || Date.now(),
  });
  scheduleSave();
};

const read = (store: Storage, scope: string): TEntry[] => {
  try {
    const text = store.getItem(storageKeyOf(scope));
    if (!text) return [];
    const stored = JSON.parse(text) as TStored;
    if (stored?.v !== CACHE_VERSION || stored.scope !== scope || !Array.isArray(stored.entries)) return [];
    const now = Date.now();
    return stored.entries.filter((e) => {
      if (!e || typeof e.h !== 'string' || !Array.isArray(e.k) || now - e.t >= MAX_AGE) return false;
      const rule = ruleFor(e.k as QueryKey);
      return Boolean(rule && (!rule.sameDay || dayOf(e.t) === dayOf(now)));
    });
  } catch {
    return [];
  }
};

/**
 * Starts keeping this person's data, putting back what was kept last time
 * (marked out of date, so it refreshes when used). Anyone else's, and any
 * older version's, is removed. Calling it again for the same person does
 * nothing; for someone else it first empties the in-memory cache.
 */
export const startPersist = (queryClient: QueryClient, scope: string): void => {
  if (client === queryClient && activeScope === scope) return;
  if (timer) saveNow();
  unsubscribe?.();
  if (lastScope && lastScope !== scope) {
    // Another person: nothing of the previous one's may show.
    queryClient.clear();
  }
  client = queryClient;
  activeScope = scope;
  lastScope = scope;
  entries = new Map();
  restored.clear();

  const store = storage();
  if (store) {
    try {
      ourKeys(store).filter((k) => k !== storageKeyOf(scope)).forEach((k) => store.removeItem(k));
    } catch {
      // Storage blocked: nothing kept to remove.
    }
    const back = read(store, scope);
    back.forEach((e) => entries.set(e.h, e));
    const cache = queryClient.getQueryCache();
    hydrate(queryClient, {
      mutations: [],
      queries: back
        .filter((e) => {
          const existing = cache.get(e.h);
          return !existing || existing.state.dataUpdatedAt < e.t;
        })
        .map((e) => {
          restored.set(e.h, e.t);
          return {
            queryKey: e.k as QueryKey,
            queryHash: e.h,
            state: {
              data: e.d,
              dataUpdateCount: 1,
              dataUpdatedAt: e.t,
              error: null,
              errorUpdateCount: 0,
              errorUpdatedAt: 0,
              fetchFailureCount: 0,
              fetchMeta: null,
              // Possibly out of date: the first page to use it asks again, and shows this meanwhile.
              isFetching: false,
              isInvalidated: true,
              isPaused: false,
              status: 'success' as const,
            },
          };
        }),
    });
  }

  unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (!event?.query) return;
    if (event.type === 'queryUpdated') {
      const { query } = event;
      if (event.action?.type === 'success') {
        remember(query);
      }
      if (restored.has(query.queryHash)) {
        if (query.state.dataUpdatedAt !== restored.get(query.queryHash) || event.action?.type === 'error') {
          restored.delete(query.queryHash);
        }
        notifyRestored();
      }
    } else if (event.type === 'queryRemoved' && restored.delete(event.query.queryHash)) {
      notifyRestored();
    }
  });
  notifyRestored();
};

/**
 * Forgets everything kept, in storage and in memory: on signing out, when a
 * session ends, or when someone else signs in. Nothing is kept again until
 * the portal starts for the next person.
 */
export const clearPersistedQueries = (): void => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  unsubscribe?.();
  unsubscribe = null;
  activeScope = null;
  entries = new Map();
  restored.clear();
  const store = storage();
  if (store) {
    try {
      ourKeys(store).forEach((k) => store.removeItem(k));
    } catch {
      // Storage blocked: nothing was kept.
    }
  }
  client?.clear();
  notifyRestored();
};

/** Whether a page is still showing data from last time while it refreshes (for a quiet "Updating…"). */
export const isRefreshingRestored = (): boolean => {
  if (!client || restored.size === 0) return false;
  const cache = client.getQueryCache();
  return Array.from(restored.keys()).some((hash) => {
    const query = cache.get(hash);
    return Boolean(query && query.state.isFetching && query.getObserversCount() > 0);
  });
};

export const useRefreshingRestored = (): boolean => {
  const [refreshing, setRefreshing] = useState(isRefreshingRestored);
  useEffect(() => {
    let alive = true;
    const check = () => alive && setRefreshing(isRefreshingRestored());
    restoredListeners.add(check);
    check();
    return () => {
      alive = false;
      restoredListeners.delete(check);
    };
  }, []);
  return refreshing;
};

/**
 * For the new portal's shell: keeps the signed-in person's lists between
 * visits. Puts last visit's data back before the first page renders (so it
 * shows at once) and returns the scope, which the shell keys its pages by so
 * nothing of one person stays on screen for another.
 */
export const usePersistedCache = (user: TCacheOwner): string | null => {
  const queryClient = useQueryClient();
  const scope = cacheScopeOf(user);
  const started = useRef<string | null>(null);
  if (scope && started.current !== scope) {
    // During render, before any page asks for anything: the pages' first render already has the data.
    started.current = scope;
    startPersist(queryClient, scope);
  }
  // Write what is pending when the tab is hidden or closed.
  useEffect(() => {
    const flush = () => {
      if (document.visibilityState === 'hidden') saveNow();
    };
    window.addEventListener('pagehide', saveNow);
    document.addEventListener('visibilitychange', flush);
    return () => {
      window.removeEventListener('pagehide', saveNow);
      document.removeEventListener('visibilitychange', flush);
    };
  }, []);
  return scope;
};

/** Tests only: back to a fresh page load. */
export const resetPersistForTests = (): void => {
  if (timer) clearTimeout(timer);
  timer = null;
  unsubscribe?.();
  unsubscribe = null;
  client = null;
  activeScope = null;
  lastScope = null;
  entries = new Map();
  restored.clear();
};
